'use client';

/**
 * ONE-FOCUS ENGINE — the v6 deck chassis (Control Room v6 §6, five laws):
 *   1. GALLERY  — crystal cards on the v4 arc + filter pills + status-count chips
 *   2. FOCUS    — 3 panes WHY / PREVIEW / DECIDE, ‹ ALL CARDS, ←→ step
 *   3. QUEUE    — counter `CARD n OF m · k TO DECIDE`; decisions auto-advance (~1.5s)
 *   4. ⚡ AUTO   — arm → confirm → sequential run with live n/m
 *   5. ◈ assistant scoped to the focused object (adapter-provided)
 * Keyboard: ←→ flick · Enter pinch · Esc back.
 * Every deck instantiates this with a real-API adapter — NO mock data ever.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useCarousel } from '@/future/physics/useCarousel';
import { EmptyState, mono, display } from '@/future/primitives';

export const CY = '#9ef0ff';
/* v6 status colors (README §3) */
export const ST: Record<string, string> = {
  new: '#9ef0ff', approved: '#9ef0ff', contacted: '#7fa9bd', quoted: '#ffc36b', proposed: '#ffc36b',
  paused: '#ffc36b', review: '#ffc36b', pending_review: '#ffc36b', draft: '#7fa9bd', building: '#ffc36b',
  won: '#5df2b4', done: '#5df2b4', live: '#5df2b4', posted: '#5df2b4', meeting: '#5df2b4', promoted: '#5df2b4',
  running: '#9ef0ff', scaling: '#9ef0ff', replied: '#9ef0ff', active: '#5df2b4', shipped: '#5df2b4', fixed: '#5df2b4',
  lost: '#ff7d8a', rejected: '#ff7d8a', failed: '#ff7d8a', passed: '#ff7d8a', not_contacted: '#7fa9bd', emailed: '#7fa9bd',
};
export const stColor = (s?: string) => ST[(s || '').toLowerCase()] || '#7fa9bd';
export const stLabel = (s?: string) => ((s || '') === 'pending_review' ? 'REVIEW' : (s || '').replace(/_/g, ' ').toUpperCase());

export interface OFItem {
  id: string;
  /** business / site badge shown top-right of the card */
  biz?: string;
  /** card headline (identity) */
  title: string;
  /** type badge under the headline */
  type?: string;
  status?: string;
  kpis: { l: string; v: string }[];
  /** payload taste (max ~2 lines) */
  taste?: string;
  /** mono foot line */
  foot?: string;
  raw: any;
}

export interface OFAdapter {
  kind: string;
  /** deck strap line, e.g. 'ONE FOCUS · 354 LEADS · HUB LIVE' */
  strap: (items: OFItem[]) => string;
  /** statuses that count as "to decide" for the queue counter + AUTO */
  decidable?: (it: OFItem) => boolean;
  Why: (p: { item: OFItem; refresh: () => void }) => ReactNode;
  Preview: (p: { item: OFItem }) => ReactNode;
  Decide: (p: { item: OFItem; refresh: () => void; advance: () => void; speak: (t: string) => void }) => ReactNode;
  auto?: { label: (n: number) => string; run: (it: OFItem) => Promise<void> };
  assistant?: (p: { item: OFItem | null }) => ReactNode;
}

/* v4/v6 arc math — verbatim from the Improve reference */
/* one card at a time (Boss 2026-07-10) — neighbors are ghost glass, content only when focused */
const AS = [1.15, .88, .66, .52, .44];
const AO = [1, .85, .45, .14, .05]; // Boss 2026-07-10: reverted one-card-only fade — he wants to SEE the side cards
function paneStyle(off: number, scrub: boolean): CSSProperties {
  const ad = Math.abs(off);
  const deg = ad <= 2 ? ad * 38 : 76 + (ad - 2) * 7;
  const th = Math.sign(off) * deg * Math.PI / 180;
  const az = Math.min(ad, 4), a0 = Math.floor(az), a1 = Math.min(a0 + 1, 4), at = az - a0;
  const sc = AS[a0] + (AS[a1] - AS[a0]) * at;
  const op = AO[a0] + (AO[a1] - AO[a0]) * at;
  return {
    position: 'absolute', left: `calc(50% + ${Math.round(660 * Math.sin(th))}px - 190px)`,
    top: 150 + Math.round(120 * (Math.cos(th) - 1)), width: 380, height: 470,
    transformOrigin: '50% 100%',
    transform: `perspective(1800px) rotateY(${Math.sign(off) * Math.min(ad, 2) * -12}deg) scale(${sc})`,
    opacity: op, zIndex: Math.round(40 - ad * 10),
    transition: scrub ? 'opacity .3s' : 'left .55s cubic-bezier(.2,.75,.25,1),top .55s cubic-bezier(.2,.75,.25,1),transform .55s cubic-bezier(.2,.75,.25,1),opacity .45s ease-out',
  };
}

export const paneBox: CSSProperties = {
  borderRadius: 14, padding: 18, boxSizing: 'border-box', position: 'relative', overflow: 'hidden',
  background: 'linear-gradient(170deg,rgba(40,140,180,.08),rgba(8,18,28,.45) 55%)',
  border: '1px solid rgba(158,240,255,.26)', boxShadow: '0 0 40px -12px rgba(0,240,255,.18)',
};
export const scanline: CSSProperties = { position: 'absolute', inset: 0, background: 'repeating-linear-gradient(180deg,rgba(140,235,255,.05) 0 1px,transparent 1px 3px)', mixBlendMode: 'screen', opacity: .5, pointerEvents: 'none' };
export const pill = (active: boolean): CSSProperties => active
  ? { padding: '6px 14px', borderRadius: 999, color: '#04121a', background: 'linear-gradient(180deg,#a8eeff,#5fd4ef)', boxShadow: '0 0 16px rgba(0,240,255,.4)', ...mono(16, '.18em'), border: 'none', transition: 'all .3s', whiteSpace: 'nowrap', cursor: 'pointer' }
  : { padding: '6px 14px', borderRadius: 999, color: '#56788c', border: '1px solid rgba(125,232,255,.16)', background: 'transparent', ...mono(16, '.18em'), transition: 'all .3s', whiteSpace: 'nowrap', cursor: 'pointer' };
export const bigVerb = (color = '#43e69a', glow = 'rgba(93,242,180,.45)'): CSSProperties => ({
  width: '100%', padding: '16px 0', borderRadius: 12, border: 'none', color: '#04121a',
  background: `linear-gradient(180deg,${color === '#43e69a' ? '#8dffd2' : '#a8eeff'},${color})`, boxShadow: `0 0 30px ${glow}`, ...mono(20, '.3em'), cursor: 'pointer',
});
export const ghostVerb: CSSProperties = { width: '100%', padding: '10px 0', marginTop: 10, borderRadius: 12, background: 'transparent', border: '1px solid rgba(255,125,138,.35)', color: '#ff7d8a', ...mono(16, '.26em'), cursor: 'pointer' };

/* v6 review-queue law: decisions speak their result through the real TTS */
const speak = (t: string) => { fetch('/api/voice/speak', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: t }) }).catch(() => { }); };

export function OneFocusDeck({ items, loaded, adapter, refresh }: { items: OFItem[]; loaded: boolean; adapter: OFAdapter; refresh: () => void }) {
  const [biz, setBiz] = useState('');
  const [sfil, setSfil] = useState('');
  const [focusIdx, setFocusIdx] = useState<number | null>(null);
  const [auto, setAuto] = useState<{ done: number; total: number } | null>(null);
  const [confirmAuto, setConfirmAuto] = useState(false);
  const [iaOpen, setIaOpen] = useState(false);
  /* ⚿-draggable focus panes + ↺ RESET LAYOUT (v6 §6 law 2) */
  const [paneOffs, setPaneOffs] = useState<Record<string, { x: number; y: number }>>({});
  const [paneGrab, setPaneGrab] = useState<{ id: string; ox: number; oy: number } | null>(null);
  useEffect(() => {
    if (!paneGrab) return;
    const move = (e: PointerEvent) => setPaneOffs(o => ({ ...o, [paneGrab.id]: { x: e.clientX - paneGrab.ox, y: e.clientY - paneGrab.oy } }));
    const up = () => setPaneGrab(null);
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  }, [paneGrab]);
  const paneDrag = (id: string) => ({
    style: {
      transform: paneOffs[id] ? `translate(${paneOffs[id].x}px,${paneOffs[id].y}px)` : 'none',
      transition: paneGrab?.id === id ? 'none' : 'transform .3s',
      zIndex: paneGrab?.id === id ? 66 : 34,
      boxShadow: paneGrab?.id === id ? '0 40px 100px -24px rgba(0,0,0,.85),0 0 70px -10px rgba(0,240,255,.45)' : undefined,
    } as CSSProperties,
    handle: (
      <span onPointerDown={(e) => { e.stopPropagation(); const c = paneOffs[id] || { x: 0, y: 0 }; setPaneGrab({ id, ox: e.clientX - c.x, oy: e.clientY - c.y }); }}
        style={{ cursor: 'grab', color: '#3d4654', fontSize: 20, userSelect: 'none', marginLeft: 'auto' }} title="move">⚿</span>
    ),
  });

  const bizes = [...new Set(items.map(i => i.biz).filter(Boolean))].sort() as string[];
  const statuses = [...new Set(items.map(i => i.status).filter(Boolean))] as string[];
  const pool = items.filter(i => (!biz || i.biz === biz) && (!sfil || i.status === sfil));
  const decidable = adapter.decidable || (() => false);
  const toDecide = pool.filter(decidable);
  const focus = focusIdx != null ? pool[Math.min(focusIdx, pool.length - 1)] : null;

  /* queue auto-advance: after a decision, step to the next undecided card (~1.5s) */
  const advance = useCallback(() => {
    setTimeout(() => {
      setFocusIdx(idx => {
        if (idx == null) return idx;
        const rest = pool.map((p, i) => ({ p, i })).filter(({ p, i }) => i !== idx && decidable(p));
        if (!rest.length) return null; // deck clear → gallery
        const next = rest.find(({ i }) => i > idx) || rest[0];
        return next.i;
      });
    }, 1500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool.length]);

  /* keyboard: ←→ flick/step · Enter pinch · Esc back */
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === 'INPUT' || (e.target as HTMLElement)?.tagName === 'TEXTAREA') return;
      if (focusIdx != null) {
        if (e.key === 'Escape') setFocusIdx(null);
        if (e.key === 'ArrowRight') setFocusIdx(i => (i == null ? i : (i + 1) % pool.length));
        if (e.key === 'ArrowLeft') setFocusIdx(i => (i == null ? i : (i - 1 + pool.length) % pool.length));
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [focusIdx, pool.length]);

  const runAuto = async () => {
    if (!adapter.auto) return;
    setConfirmAuto(false);
    const targets = toDecide;
    setAuto({ done: 0, total: targets.length });
    for (let i = 0; i < targets.length; i++) {
      try { await adapter.auto.run(targets[i]); } catch { /* logged upstream */ }
      setAuto({ done: i + 1, total: targets.length });
    }
    refresh();
    setTimeout(() => setAuto(null), 2500);
  };

  return (
    <div className="absolute inset-0" style={{ top: 190, bottom: 84 }}> {/* clear 2×-font chrome + legends (Boss 2026-07-10) */}
      {focus ? (
        <div className="absolute inset-0 flex flex-col" style={{ gap: 14 }}>
          {/* focus header + queue counter */}
          <div className="flex items-center" style={{ gap: 14 }}>
            <button onMouseUp={() => setFocusIdx(null)} data-lock="ALL CARDS"
              style={{ ...mono(16, '.22em'), color: CY, border: '1px solid rgba(125,232,255,.25)', borderRadius: 999, padding: '7px 15px', background: 'transparent', cursor: 'pointer' }}>‹ ALL CARDS</button>
            <span style={{ ...display(38, 300), color: '#e9f9ff', textShadow: '0 0 18px rgba(0,240,255,.25)' }}>{focus.title}</span>
            {focus.type && <span style={{ ...mono(15, '.24em'), color: CY }}>{focus.type}</span>}
            <span style={{ flex: 1 }} />
            <span style={{ ...mono(15, '.2em'), color: '#56788c' }}>CARD {(focusIdx ?? 0) + 1} OF {pool.length} · {toDecide.length} TO DECIDE</span>
            {Object.keys(paneOffs).length > 0 && (
              <button onMouseUp={() => setPaneOffs({})}
                style={{ ...mono(14, '.2em'), color: '#9ef0ff', border: '1px solid rgba(125,232,255,.25)', borderRadius: 999, padding: '4px 12px', background: 'transparent', cursor: 'pointer' }}>↺ RESET LAYOUT</button>
            )}
            {focus.status && <span style={{ ...mono(14, '.2em'), color: stColor(focus.status), border: `1px solid color-mix(in srgb, ${stColor(focus.status)} 45%, transparent)`, borderRadius: 999, padding: '3px 10px' }}>{stLabel(focus.status)}</span>}
          </div>
          {/* WHY / PREVIEW / DECIDE — v6 pane ratio 470:680:470 */}
          <div className="flex-1 min-h-0 grid" style={{ gridTemplateColumns: '360px 1fr 340px', gap: 14 }}>
            <div style={{ ...paneBox, ...paneDrag('why').style }} className="flex flex-col min-h-0 overflow-auto"><div style={scanline} />
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}><span style={{ ...mono(16, '.3em'), color: 'var(--uif-t4)' }}>WHY</span>{paneDrag('why').handle}</div>
              <adapter.Why item={focus} refresh={refresh} />
            </div>
            <div style={{ ...paneBox, ...paneDrag('preview').style }} className="flex flex-col min-h-0"><div style={scanline} />
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}><span style={{ ...mono(16, '.3em'), color: 'var(--uif-t4)' }}>PREVIEW</span>{paneDrag('preview').handle}</div>
              <div className="flex-1 min-h-0 overflow-auto"><adapter.Preview item={focus} /></div>
            </div>
            <div style={{ ...paneBox, ...paneDrag('decide').style }} className="flex flex-col min-h-0 overflow-auto"><div style={scanline} />
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}><span style={{ ...mono(16, '.3em'), color: 'var(--uif-t4)' }}>DECIDE</span>{paneDrag('decide').handle}</div>
              <adapter.Decide item={focus} refresh={refresh} advance={advance} speak={speak} />
              {adapter.assistant && (
                <button onMouseUp={() => setIaOpen(o => !o)} data-lock="ASK"
                  style={{ width: '100%', padding: '12px 0', marginTop: 12, borderRadius: 12, background: iaOpen ? 'linear-gradient(180deg,#a8eeff,#5fd4ef)' : 'transparent', color: iaOpen ? '#04121a' : CY, border: iaOpen ? 'none' : '1px solid rgba(125,232,255,.3)', ...mono(17, '.24em'), cursor: 'pointer' }}>
                  ◈ ASK THE ASSISTANT
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <GalleryView pool={pool} loaded={loaded} bizes={bizes} biz={biz} setBiz={setBiz}
          statuses={statuses} sfil={sfil} setSfil={setSfil} strap={adapter.strap(items)}
          toDecide={toDecide.length} auto={auto} confirmAuto={confirmAuto} setConfirmAuto={setConfirmAuto}
          hasAuto={!!adapter.auto} autoLabel={adapter.auto?.label(toDecide.length) || ''} runAuto={runAuto}
          onFocus={(i: number) => setFocusIdx(i)} iaOpen={iaOpen} setIaOpen={setIaOpen} hasAssistant={!!adapter.assistant} />
      )}
      {/* ◈ assistant slab (iaStyle verbatim) */}
      {adapter.assistant && (
        <div style={{
          position: 'fixed', right: 36, bottom: 130, width: 560, height: 640, maxHeight: 'calc(100vh - 180px)', zIndex: 85,
          borderRadius: 16, border: '1px solid rgba(125,232,255,.4)',
          background: 'linear-gradient(170deg,rgba(40,140,180,.12),rgba(4,10,18,.95) 55%)',
          boxShadow: '0 40px 100px -24px rgba(0,0,0,.9),0 0 70px -16px rgba(0,240,255,.35)',
          padding: 18, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 12,
          opacity: iaOpen ? 1 : 0, transform: iaOpen ? 'translateY(0) scale(1)' : 'translateY(40px) scale(.92)',
          pointerEvents: iaOpen ? 'auto' : 'none', visibility: iaOpen ? 'visible' : 'hidden',
          transition: 'opacity .35s,transform .35s cubic-bezier(.25,.9,.3,1),visibility .35s',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ ...mono(18, '.24em'), color: CY, textShadow: '0 0 10px rgba(0,240,255,.4)' }}>◈ ASSISTANT</span>
            <span style={{ ...mono(14, '.18em'), color: '#49677a' }}>{focus ? focus.title.toUpperCase().slice(0, 40) : adapter.kind.toUpperCase()}</span>
            <span style={{ flex: 1 }} />
            <button onMouseUp={() => setIaOpen(false)} aria-label="Close" style={{ ...mono(18), color: '#56788c', background: 'transparent', border: 'none', cursor: 'pointer' }}>✕</button>
          </div>
          <div className="flex-1 min-h-0">{iaOpen && adapter.assistant({ item: focus })}</div>
        </div>
      )}
    </div>
  );
}

function GalleryView({ pool, loaded, bizes, biz, setBiz, statuses, sfil, setSfil, strap, toDecide, auto, confirmAuto, setConfirmAuto, hasAuto, autoLabel, runAuto, onFocus, iaOpen, setIaOpen, hasAssistant }: any) {
  const car = useCarousel(Math.max(pool.length, 1), (i) => pool[i] && onFocus(i));
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = host.current; if (!el) return;
    const wheel = (e: WheelEvent) => car.onWheel(e);
    el.addEventListener('wheel', wheel, { passive: false });
    return () => el.removeEventListener('wheel', wheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car.onWheel]);

  return (
    <div ref={host} className="absolute inset-0"
      onPointerDown={car.onPointerDown} onPointerMove={car.onPointerMove} onPointerUp={(e) => car.onPointerUp(e)}>
      {/* filter pills */}
      <div className="absolute top-0 flex justify-center flex-wrap" style={{ left: 180, right: 260, gap: 8, zIndex: 50 }}>
        <button style={pill(!biz)} onMouseUp={() => setBiz('')}>ALL{!biz && pool.length ? ` · ${pool.length}` : ''}</button>
        {bizes.map((b: string) => (
          <button key={b} style={pill(biz === b)} onMouseUp={() => setBiz(biz === b ? '' : b)}>{b.toUpperCase().replace('.CA', '')}</button>
        ))}
        {statuses.length > 1 && <span style={{ width: 14 }} />}
        {statuses.length > 1 && statuses.map((s: string) => (
          <button key={s} style={{ ...pill(sfil === s), color: sfil === s ? '#04121a' : stColor(s) }} onMouseUp={() => setSfil(sfil === s ? '' : s)}>{stLabel(s)}</button>
        ))}
      </div>
      {/* strap + AUTO + assistant */}
      <div className="absolute right-2 top-0 flex items-center" style={{ gap: 10, zIndex: 50 }}>
        {hasAssistant && <button onMouseUp={() => setIaOpen(!iaOpen)} style={pill(iaOpen)}>◈ ASSISTANT</button>}
        {hasAuto && (auto ? (
          <span style={{ ...mono(16, '.18em'), color: '#5df2b4', padding: '6px 14px', borderRadius: 999, border: '1px solid rgba(93,242,180,.4)' }}>⚡ {auto.done}/{auto.total}</span>
        ) : confirmAuto ? (
          <button onMouseUp={runAuto} style={{ padding: '6px 14px', borderRadius: 999, color: '#04121a', background: 'linear-gradient(180deg,#8dffd2,#43e69a)', boxShadow: '0 0 20px rgba(93,242,180,.5)', ...mono(16, '.18em'), border: 'none', cursor: 'pointer' }}>⚡ ✓ CONFIRM {toDecide}?</button>
        ) : (
          <button onMouseUp={() => toDecide && setConfirmAuto(true)} style={{ ...pill(false), color: toDecide ? '#5df2b4' : '#3f5665', borderColor: toDecide ? 'rgba(93,242,180,.35)' : 'rgba(125,232,255,.1)' }}>⚡ {autoLabel}</button>
        ))}
      </div>
      <div className="absolute left-2 bottom-0" style={{ ...mono(14, '.24em'), color: '#49677a', zIndex: 50 }}>{strap}</div>

      {/* crystal cards */}
      {pool.map((it: OFItem, i: number) => {
        const off = car.offsetOf(i);
        if (Math.abs(off) > 4.5) return null;
        const focused = Math.abs(off) < .4;
        const far = Math.abs(off) >= 1.6;
        return (
          <div key={it.id} style={paneStyle(off, car.scrubbing)}>
            <div onMouseUp={() => car.tap(i)} role="button" className="w-full h-full" style={{
              borderRadius: 14, padding: 18, boxSizing: 'border-box', overflow: 'hidden', position: 'relative',
              background: 'linear-gradient(170deg,rgba(40,140,180,.08),rgba(8,18,28,.45) 55%)',
              border: `1px solid rgba(158,240,255,${focused ? .55 : .26})`,
              boxShadow: focused ? '0 0 70px -8px rgba(0,240,255,.4)' : '0 0 40px -12px rgba(0,240,255,.18)',
              transition: 'border-color .4s,box-shadow .4s', cursor: 'pointer',
            }}>
              <div style={scanline} />
              <div style={{ position: 'absolute', top: 0, left: '12%', right: '12%', height: 1, background: `linear-gradient(90deg,transparent,${CY},transparent)` }} />
              <div style={{ opacity: far ? 0 : 1, transition: 'opacity .4s' }}>
              <div className="flex items-center" style={{ gap: 9 }}>
                <span style={{ ...display(26, 500), color: '#e9f9ff', textShadow: '0 0 10px rgba(0,240,255,.2)', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.title}</span>
                {it.status && <span style={{ ...mono(13, '.2em'), color: stColor(it.status), border: `1px solid color-mix(in srgb, ${stColor(it.status)} 45%, transparent)`, borderRadius: 999, padding: '2px 8px', flexShrink: 0 }}>{stLabel(it.status)}</span>}
              </div>
              {it.type && <div style={{ ...mono(14, '.24em'), color: CY, marginTop: 8 }}>{it.type}</div>}
              {it.biz && <div style={{ ...mono(13, '.18em'), color: '#7fa9bd', marginTop: 12 }}>{it.biz.toUpperCase()}</div>}
              <div className="flex" style={{ gap: 18, marginTop: 14, flexWrap: 'wrap' }}>
                {it.kpis.slice(0, 3).map(k => (
                  <div key={k.l}>
                    <div style={{ ...mono(13, '.18em'), color: '#49677a', marginBottom: 2 }}>{k.l}</div>
                    <div style={{ fontWeight: 200, fontSize: 38, color: '#aef0ff', textShadow: '0 0 14px rgba(0,240,255,.35)' }}>{k.v}</div>
                  </div>
                ))}
              </div>
              {it.taste && (
                <div style={{ marginTop: 14, borderTop: '1px solid rgba(125,232,255,.12)', paddingTop: 10 }}>
                  <div style={{ ...display(23, 300), color: '#c4d8e4', lineHeight: 1.5, maxHeight: 120, overflow: 'hidden' }}>{it.taste}</div>
                </div>
              )}
              {it.foot && <div style={{ position: 'absolute', left: 18, right: 18, bottom: 40, ...mono(14, '.12em'), color: '#49677a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.foot}</div>}
              </div>{/* /far-fade wrapper */}
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: 14, textAlign: 'center', ...mono(15, '.3em'), color: CY, textShadow: '0 0 12px rgba(0,240,255,.5)', opacity: focused ? 1 : 0, transition: 'opacity .4s' }}>PINCH TO FOCUS</div>
            </div>
          </div>
        );
      })}
      {loaded && pool.length === 0 && <EmptyState what="cards" />}
      {/* status-count chips (bottom) */}
      <div className="absolute left-0 right-0 flex justify-center" style={{ bottom: 6, gap: 26 }}>
        {statuses.slice(0, 5).map((s: string) => (
          <span key={s} style={{ ...mono(15, '.22em'), color: '#56788c' }}>
            {stLabel(s)} · <span style={{ color: stColor(s) }}>{pool.filter((r: OFItem) => r.status === s).length}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
